import { detectCountryFormat, formatAddressParts, } from "@/shared/utils/formatAddress";
import { availableAddressFieldKeys, carryStreetToLayout, hasAddressField, splitStreetLine, streetNumberOrder, toAddressLayout, } from "@/shared/utils/addressFieldConfig";
import { STORED_COUNTRY_NAMES, isKnownAddressCountry, } from "@/shared/utils/addressCountry";
// Multi-line preview segments — ordered by the ADDRESS country's own format
// (an NL address previews Dutch-style even in an FR workspace).
export function addressParts(a, names = STORED_COUNTRY_NAMES) {
    return formatAddressParts(a, detectCountryFormat(a.country, a.region), names);
}
export function findPrimaryId(addresses) {
    return addresses.find((a) => a.isPrimary)?.id ?? "";
}
// Mirrors BFF OrganisationLayoutBuilder logic: Headquarters first, then first address.
export function findMainAddress(addresses) {
    return (addresses.find((a) => a.type === "Headquarters") ?? addresses[0] ?? null);
}
export function addressBadgeTheme(type) {
    switch (type) {
        case "Headquarters":
            return "green";
        case "Branch":
            return "violet";
        case "Billing":
            return "orange";
        default:
            return "neutral";
    }
}
// Writes the parsed address into the workspace's field set and empties the fields outside it:
// a parsed value there could be neither read nor corrected, and the save keeps it
// (CONSTRAINT: toAddressLayout), so the replaced address's own complement would outlive it.
export function applyParsedAddress(raw, target, workspaceCountry, fallbackCountry, names = STORED_COUNTRY_NAMES) {
    const parsed = parseAddressQuery(raw, names, fallbackCountry);
    const country = parsed.country || fallbackCountry || "";
    const available = workspaceCountry ? availableAddressFieldKeys(workspaceCountry) : null;
    const canWrite = (key) => !available || available.has(key);
    target.streetName = "";
    target.line1 = parsed.line1 ?? "";
    target.line2 = canWrite("line2") ? parsed.line2 ?? "" : "";
    target.line3 = canWrite("line3") ? parsed.line3 ?? "" : "";
    if (!canWrite("region"))
        target.region = "";
    if (available?.has("houseNumber"))
        Object.assign(target, splitIntoStreetAndNumber(parsed.line1 ?? "", country));
    if (canWrite("postalCode"))
        target.postalCode = parsed.postalCode ?? "";
    if (canWrite("city"))
        target.city = parsed.city ?? "";
    if (canWrite("country"))
        target.country = country;
}
// CONSTRAINT: the projection runs HERE, not only at the save-time one every caller already has -
// the modal must OPEN on the folded street line, or its Adresse input would contradict the preview
// right under it. A value the layout omits stays uneditable here (OP-8017) but is kept by the save
// (CONSTRAINT: toAddressLayout), so only a NEWLY PARSED one is kept out below.
export function seedAddressDetails(form, parsed, fallbackCountry, workspaceCountry) {
    const own = { ...form };
    carryStreetToLayout(own);
    const seeded = {
        line1: own.line1,
        line2: own.line2 || (hasAddressField(workspaceCountry, "line2") ? parsed.line2 ?? "" : ""),
        line3: own.line3 || (hasAddressField(workspaceCountry, "line3") ? parsed.line3 ?? "" : ""),
        streetName: "",
        houseNumber: own.houseNumber,
        houseNumberSuffix: own.houseNumberSuffix,
        postalCode: own.postalCode || parsed.postalCode || "",
        city: own.city || parsed.city || "",
        region: own.region,
        country: own.country || parsed.country || fallbackCountry,
    };
    if (!seeded.line1 && parsed.line1) {
        if (hasAddressField(workspaceCountry, "houseNumber") && !seeded.houseNumber) {
            Object.assign(seeded, splitIntoStreetAndNumber(parsed.line1, seeded.country));
        }
        else {
            seeded.line1 = parsed.line1;
        }
    }
    return toAddressLayout(seeded, workspaceCountry);
}
function splitIntoStreetAndNumber(line, country) {
    const { street, houseNumber, houseNumberSuffix } = splitStreetLine(line, streetNumberOrder(country));
    return { line1: street, houseNumber, houseNumberSuffix };
}
// Tried only for a Dutch address (the country typed last, else the address's own
// country): elsewhere a city such as "La Hulpe" or "De Panne" would read as the
// postcode letters.
const NL_POSTCODE_CITY = /(?<!\d)([1-9]\d{3})\s?([A-Z]{2})(?![A-Za-z])\s+([^,]+)/i;
// Lookarounds prevent matching partial digits inside a longer number (e.g. "122234" must not match "22234").
const DIGIT_POSTCODE_CITY = /(?<!\d)(\d{4,5})(?!\d)\s+([^,]+)/;
function matchPostcodeCity(raw, names, countryHint) {
    const segments = raw.split(",");
    const typedCountry = segments.length > 1 ? names.canonical(segments[segments.length - 1]) : "";
    const country = isKnownAddressCountry(typedCountry) ? typedCountry : countryHint;
    const nl = detectCountryFormat(country) === "NL" ? raw.match(NL_POSTCODE_CITY) : null;
    if (nl) {
        return {
            index: nl.index,
            length: nl[0].length,
            postalCode: `${nl[1]} ${nl[2].toUpperCase()}`,
            city: nl[3],
        };
    }
    const digits = raw.match(DIGIT_POSTCODE_CITY);
    return digits
        ? { index: digits.index, length: digits[0].length, postalCode: digits[1], city: digits[2] }
        : null;
}
// Parses a formatted address string (line2/line3 included).
// Layout shape: "line1[, line2[, line3]], postalCode city[, country]".
export function parseAddressQuery(raw, names = STORED_COUNTRY_NAMES, countryHint = "") {
    const match = matchPostcodeCity(raw, names, countryHint);
    if (match) {
        const beforePostal = raw.slice(0, match.index).replace(/,?\s*$/, "").trim();
        const lineParts = beforePostal
            ? beforePostal
                .split(",")
                .map((p) => p.trim())
                .filter(Boolean)
            : [];
        const afterPostal = raw.slice(match.index + match.length);
        const countryMatch = afterPostal.match(/,\s*(.+)$/);
        return {
            line1: lineParts[0] || undefined,
            line2: lineParts[1] || undefined,
            line3: lineParts[2] || undefined,
            postalCode: match.postalCode,
            city: match.city.trim(),
            country: countryMatch ? names.canonical(countryMatch[1]) : undefined,
        };
    }
    return {
        line1: raw || undefined,
        line2: undefined,
        line3: undefined,
        postalCode: undefined,
        city: undefined,
        country: undefined,
    };
}
